import { useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import { Avatar } from '../../design-system/components';
import { useImpersonatableUsers, useImpersonate, type ImpersonatableUser } from '../useAuth';
import { roleLabel } from '../roleLabels';
import '../../design-system/tokens.css';

interface Props {
  open: boolean;
  onClose: () => void;
}

function lastSeen(iso: string | null): string {
  if (!iso) return 'ни разу не входил';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'ни разу не входил';
  return `был ${d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })}`;
}

function matches(user: ImpersonatableUser, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  // Почты у сотрудника может не быть (приглашён по СМС) — ищем и по телефону,
  // причём по цифрам: человек набирает «921», а не «+7 (921)».
  const digits = q.replace(/\D/g, '');

  return (
    user.name.toLowerCase().includes(q) ||
    (user.email?.toLowerCase().includes(q) ?? false) ||
    (digits.length > 0 && (user.phone ?? '').replace(/\D/g, '').includes(digits)) ||
    user.roles.some((r) => roleLabel([r]).toLowerCase().includes(q))
  );
}

/**
 * Переключатель «войти как пользователь»: владелец выбирает сотрудника и видит
 * админку его глазами. Пароль не спрашивается — право даёт роль владельца,
 * сервер подменяет пользователя в самой сессии.
 *
 * После переключения страница перезагружается целиком: у нового пользователя
 * другие права и другой лендинг, а половина экранов держит своё состояние вне
 * TanStack Query (сторы, localStorage) — чистая перезагрузка исключает
 * залипание чужих данных на экране.
 */
export function ImpersonatePicker({ open, onClose }: Props) {
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { data: users, isLoading } = useImpersonatableUsers(open);
  const impersonate = useImpersonate();

  const visible = useMemo(() => (users ?? []).filter((u) => matches(u, query)), [users, query]);

  if (!open) return null;

  async function pick(user: ImpersonatableUser) {
    setError(null);
    try {
      await impersonate.mutateAsync(user.id);
      // Полная перезагрузка под новой личиной — с домашней страницы,
      // потому что текущий экран сотруднику может быть недоступен.
      window.location.assign('/');
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      setError(e?.response?.data?.message ?? 'Не удалось переключиться. Попробуйте ещё раз.');
    }
  }

  return (
    <div
      className="ops-skin fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 pt-[10vh] text-ops-text"
      onClick={onClose}
    >
      <div
        className="flex max-h-[70vh] w-full max-w-lg flex-col overflow-hidden rounded-ops-lg bg-ops-surface shadow-ops-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-ops-border px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-ops-text">Войти как пользователь</h2>
            <p className="mt-0.5 text-sm text-ops-text-secondary">
              Вы увидите админку его глазами. Пароль не нужен, вернуться к себе — одной кнопкой.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Закрыть"
            className="-mr-1 -mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-ops-md text-ops-text-secondary hover:bg-ops-surface-2 hover:text-ops-text"
          >
            <X size={18} />
          </button>
        </div>

        <div className="border-b border-ops-border px-5 py-3">
          <div className="relative">
            <Search
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ops-text-secondary"
            />
            <input
              type="search"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Имя, почта или роль"
              className="w-full rounded-ops-sm border border-ops-border bg-ops-surface py-2 pl-9 pr-3 text-sm text-ops-text focus:border-ops-primary focus:outline-none"
            />
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-auto px-2 py-2">
          {isLoading && <p className="px-3 py-6 text-center text-sm text-ops-text-secondary">Загружаю…</p>}

          {!isLoading && visible.length === 0 && (
            <p className="px-3 py-6 text-center text-sm text-ops-text-secondary">
              {query ? 'Никого не нашлось.' : 'Других пользователей пока нет.'}
            </p>
          )}

          {visible.map((user) => (
            <button
              key={user.id}
              type="button"
              disabled={impersonate.isPending}
              onClick={() => pick(user)}
              className="flex w-full items-center gap-3 rounded-ops-md px-3 py-2.5 text-left transition-colors hover:bg-ops-surface-2 disabled:opacity-50"
            >
              <Avatar name={user.name} src={user.avatar_url ?? undefined} size="sm" />
              <span className="min-w-0 flex-1 leading-tight">
                <span className="block truncate text-sm font-medium text-ops-text">{user.name}</span>
                {/* Почты может не быть вовсе — тогда опознавательный знак телефон */}
                <span className="block truncate text-xs text-ops-text-secondary">
                  {user.email ?? user.phone ?? '—'}
                </span>
              </span>
              <span className="shrink-0 text-right leading-tight">
                <span className="block text-xs font-medium text-ops-text-secondary">
                  {roleLabel(user.roles)}
                </span>
                <span className="block text-[11px] text-ops-text-secondary/70">
                  {lastSeen(user.last_login_at)}
                </span>
              </span>
            </button>
          ))}
        </div>

        {error && (
          <div className="border-t border-ops-border px-5 py-3">
            <p className="rounded-ops-sm bg-ops-danger-soft px-3 py-2 text-sm text-ops-danger">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
}
